import { DOCUMENT_CATEGORIES } from '@/api/services/documents.service'
import {
  getDocumentUploadErrorMessage,
  getDocumentUploadHelpText,
  getDocumentUploadMaxFileSizeLabel,
} from '@/features/documents/utils/documentUtils'

/** Accept attribute for file input (MIME types). */
export const DOCUMENT_UPLOAD_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,image/avif,application/pdf'

/** Max file size in bytes (20 MB). */
export const DOCUMENT_UPLOAD_MAX_FILE_SIZE = 20 * 1024 * 1024

/** Max number of files per upload batch — matches backend UPLOAD_MAX_FILES. */
export const DOCUMENT_UPLOAD_MAX_FILES = 10

/** Allowed file extensions for validation. */
export const DOCUMENT_UPLOAD_ACCEPT_EXT = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.avif',
]

export interface DocumentCategoryOption {
  value: string
  label: string
}

const CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
  cv: 'CV / Resume',
  'offer-letter': 'Offer Letter',
  cnic: 'CNIC / ID',
  policies: 'Policies',
  contracts: 'Contracts',
  'onboarding-documents': 'Onboarding',
  'health-documents': 'Health',
  'exit-documents': 'Exit',
}

function categoryToLabel(category: string): string {
  return (
    CATEGORY_LABEL_OVERRIDES[category] ??
    category
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  )
}

/** Select options for document category (upload or filter). Include empty option for "All categories" when includeAll is true. */
export function getDocumentCategoryOptions(
  includeAll = false,
): DocumentCategoryOption[] {
  const options: DocumentCategoryOption[] = DOCUMENT_CATEGORIES.map((c) => ({
    value: c,
    label: categoryToLabel(c),
  }))
  if (includeAll) {
    return [{ value: '', label: 'All categories' }, ...options]
  }
  return options
}

/** Format category for display. */
export function formatDocumentCategory(category: string): string {
  return categoryToLabel(category)
}

/** Validate files for count, size, and type. Returns error message or null. */
export function validateDocumentFiles(files: File[]): string | null {
  if (files.length > DOCUMENT_UPLOAD_MAX_FILES) {
    return `You can upload at most ${DOCUMENT_UPLOAD_MAX_FILES} files at once`
  }
  for (const file of files) {
    if (file.size > DOCUMENT_UPLOAD_MAX_FILE_SIZE) {
      return `"${file.name}" exceeds the maximum allowed size of ${getDocumentUploadMaxFileSizeLabel()}`
    }
    const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
    if (!DOCUMENT_UPLOAD_ACCEPT_EXT.includes(ext)) {
      return `"${file.name}" has an unsupported type. Use PDF or images.`
    }
  }
  return null
}

/** Format file size for display. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export { getDocumentUploadErrorMessage, getDocumentUploadHelpText }
